import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Alert
} from '@mui/material';
import { quickCreateUser } from '../utils/createUser';

const UserCreator = ({ open, onClose, firebaseUid }) => {
  const [userData, setUserData] = useState({
    firebase_uid: firebaseUid || '',
    email: '',
    name: '',
    role: 'school_admin',
    school_id: 'school_1_id',
    employee_id: '',
    pin_code: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleCreate = async () => {
    setLoading(true);
    setError('');
    
    try {
      let result;
      
      switch (userData.role) {
        case 'developer':
          result = await quickCreateUser.developer(
            userData.firebase_uid,
            userData.email,
            userData.name
          );
          break;
        case 'school_admin':
          result = await quickCreateUser.schoolAdmin(
            userData.firebase_uid,
            userData.email,
            userData.name,
            userData.school_id
          );
          break;
        case 'school_moderator':
          result = await quickCreateUser.schoolModerator(
            userData.firebase_uid,
            userData.email,
            userData.name,
            userData.school_id
          );
          break;
        case 'teacher':
          result = await quickCreateUser.teacher(
            userData.firebase_uid,
            userData.email,
            userData.name,
            userData.school_id,
            userData.employee_id,
            userData.pin_code
          );
          break;
        default:
          throw new Error('Invalid role');
      }
      
      console.log('User created successfully:', result);
      setSuccess(true);
      
      // Close dialog after 2 seconds
      setTimeout(() => {
        onClose();
        window.location.reload(); // Refresh to retry login
      }, 2000);
      
    } catch (error) {
      console.error('Error creating user:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (event) => {
    setUserData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create User in Database</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {success ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              User created successfully! The page will refresh automatically.
            </Alert>
          ) : null}
          
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            This user exists in Firebase Auth but not in our database. Create a database record to complete the setup.
          </Typography>
          
          <TextField
            fullWidth
            label="Firebase UID"
            value={userData.firebase_uid}
            onChange={handleChange('firebase_uid')}
            disabled
            sx={{ mb: 2 }}
            helperText="This is your Firebase Authentication UID"
          />
          
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={userData.email}
            onChange={handleChange('email')}
            required
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            label="Name"
            value={userData.name}
            onChange={handleChange('name')}
            required
            sx={{ mb: 2 }}
          />
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={userData.role}
              label="Role"
              onChange={handleChange('role')}
            >
              <MenuItem value="developer">Developer</MenuItem>
              <MenuItem value="school_admin">School Admin</MenuItem>
              <MenuItem value="school_moderator">School Moderator</MenuItem>
              <MenuItem value="teacher">Teacher</MenuItem>
            </Select>
          </FormControl>
          
          {userData.role !== 'developer' && (
            <TextField
              fullWidth
              label="School ID"
              value={userData.school_id}
              onChange={handleChange('school_id')}
              required
              sx={{ mb: 2 }}
              helperText="e.g., school_1_id"
            />
          )}
          
          {userData.role === 'teacher' && (
            <>
              <TextField
                fullWidth
                label="Employee ID"
                value={userData.employee_id}
                onChange={handleChange('employee_id')}
                sx={{ mb: 2 }}
                helperText="e.g., EMP001"
              />
              
              <TextField
                fullWidth
                label="PIN Code"
                value={userData.pin_code}
                onChange={handleChange('pin_code')}
                inputProps={{ maxLength: 4 }}
                sx={{ mb: 2 }}
                helperText="4-digit PIN for device login"
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleCreate} 
          variant="contained" 
          disabled={loading || !userData.firebase_uid || !userData.email || !userData.name}
        >
          {loading ? 'Creating...' : 'Create User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserCreator;